// Desk build only, inside Route Desk's panel: checking a mobile-money payment
// for an order until it is known whether it went through.
//
// Why (DECISIONS.md 2026-10-12 "payment status in the panel"): the phone
// app learns the outcome from a push message that only reaches the CHE's
// phone, and its own fallback check read the server's answer one level too
// shallow, so the officer's panel stayed on "Processing payment" for ever.
//
// The server's answer to POST /orders/order_payment_status/ {order_id} is
//   { data: { order: { name, display_name, state, invoice_ids, ... },
//             invoice: [...], payment: { id, status, transaction_id, amount },
//             route: { delivery_start, delivery_end, id } } }
// payment.status is "pending" or "received" while waiting, then
// "successful" or "failed" (MTN can send "FAILED"). "successful" is saved a
// few seconds BEFORE Odoo confirms and invoices the order, and that step
// can fail, so paid-and-confirmed needs the order in state sale/done with an
// invoice, the same test as the phone app's own success path.
//
// Everything here is plain functions and localStorage; the polling itself is
// the saga in services/sagas/payments/deskPayment.js, the screen is
// components/DeskPaymentStatus.

import { PHONE_PATTERN, postToRouteDesk, routeDeskData } from "./deskLoginPhone";

export const POLL_INTERVAL_MS = 10 * 1000;
// Stop asking after this long without a definite answer.
export const POLL_CAP_MS = 10 * 60 * 1000;
// Paid but the order not confirmed yet: keep asking this much longer.
export const UNCONFIRMED_GRACE_MS = 60 * 1000;
// A saved payment older than this is dropped on app start (the cap plus a
// margin): Orders is then the place to look.
export const STALE_AFTER_MS = 30 * 60 * 1000;

// ---------------------------------------------------------------------------
// 1. Reading the server's answer.

// Returns one of:
//   "pending"           payment still waiting for the CHE / the provider
//   "failed"            the payment failed
//   "paid_confirmed"    paid, and the order is confirmed and invoiced
//   "paid_unconfirmed"  paid, but the order is not confirmed yet
//   "unknown"           the answer is not in the expected shape
export const paymentOutcome = (body) => {
  const data = body && typeof body === "object" ? body.data : null;
  if (!data || typeof data !== "object") return "unknown";
  const payment = data.payment;
  if (!payment || typeof payment !== "object" || typeof payment.status !== "string") return "unknown";
  const status = payment.status.trim().toLowerCase();
  if (status === "failed") return "failed";
  if (status === "pending" || status === "received") return "pending";
  if (status !== "successful") return "unknown";
  const order = data.order || {};
  const state = typeof order.state === "string" ? order.state.toLowerCase() : "";
  const invoiced = Array.isArray(order.invoice_ids) && order.invoice_ids.length > 0;
  return (state === "sale" || state === "done") && invoiced ? "paid_confirmed" : "paid_unconfirmed";
};

// ---------------------------------------------------------------------------
// 2. When to ask again, and when to stop.

// One decision after each check. `outcome` is paymentOutcome's answer, or
// "error" when the request failed, or "skipped" when it was not sent (signed
// out). `unconfirmedSince` is when "paid_unconfirmed" was first seen (null
// until then); once seen it sticks, so a later error never turns a payment
// that was received into "not confirmed yet".
// Returns either
//   { wait: <ms>, phase: "checking" | "confirming", unconfirmedSince }
// or
//   { done: "paid" | "failed" | "unconfirmed" | "timeout" }
export const nextPollStep = ({ outcome, now, startedAt, unconfirmedSince = null }) => {
  if (outcome === "paid_confirmed") return { done: "paid" };
  if (outcome === "failed") return { done: "failed" };
  const since = outcome === "paid_unconfirmed" && unconfirmedSince === null ? now : unconfirmedSince;
  if (since !== null) {
    if (now - since >= UNCONFIRMED_GRACE_MS) return { done: "unconfirmed" };
    return { wait: POLL_INTERVAL_MS, phase: "confirming", unconfirmedSince: since };
  }
  if (now - startedAt >= POLL_CAP_MS) return { done: "timeout" };
  return { wait: POLL_INTERVAL_MS, phase: "checking", unconfirmedSince: null };
};

// ---------------------------------------------------------------------------
// 3. The payments in flight, kept in localStorage so a reload, closing the
//    panel or a CHE switch in Route Desk does not lose them. A small list,
//    one entry per order, so one payment never replaces another and every
//    tab of the panel sees the same list (the `storage` event).
//
// Entry: { orderId, orderName, cheId, cheName, startedAt,
//          ended       the check stopped without a definite answer
//          finalShown  the officer closed that unclear end's screen
//          paid, unconfirmed, paidAt   see "paid marker" below }
// An entry is saved the moment the payment request is SENT (not when it is
// accepted): if that request fails in a way that may still have reached
// the provider, the entry stays and the order is checked.

export const IN_FLIGHT_KEY = "he-desk-payments-in-flight";

const positiveId = (value) => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : null;
};

// The entry saved when the payment request is sent.
export const newInFlightRecord = ({ orderId, orderName, che, now }) => ({
  orderId: positiveId(orderId),
  orderName: typeof orderName === "string" ? orderName : null,
  cheId: positiveId(che?.odoo_id),
  cheName: typeof che?.name === "string" ? che.name : null,
  startedAt: now,
  ended: false,
  finalShown: false,
});

const validRecord = (record) =>
  !!record && typeof record === "object" && positiveId(record.orderId) !== null &&
  Number.isFinite(record.startedAt);

const listeners = new Set();
const notify = () => listeners.forEach((listener) => listener());

// Calls `listener` whenever the list changes, in this tab or another one.
// Returns the unsubscribe function.
export const subscribeSavedPayments = (listener, win = window) => {
  const onStorage = (event) => {
    if (!event || event.key === IN_FLIGHT_KEY || event.key === null) listener();
  };
  listeners.add(listener);
  win.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    win.removeEventListener("storage", onStorage);
  };
};

// Even reaching window.localStorage can throw (blocked site data).
const defaultStorage = () => {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
};

// This tab's own copy of the list, used when localStorage cannot be written
// (full or blocked): the block then still holds in this tab. While writes
// work, localStorage alone is the truth (another tab may have removed an
// entry), so the copy is only merged in after a failed write.
let memoryList = [];
let lastWriteFailed = false;
export const resetMemoryForTests = () => { memoryList = []; lastWriteFailed = false; };

const readStorage = (storage) => {
  try {
    const list = JSON.parse(storage.getItem(IN_FLIGHT_KEY));
    return Array.isArray(list) ? list.filter(validRecord) : [];
  } catch (e) {
    return null;
  }
};

export const loadAll = (storage = defaultStorage()) => {
  const stored = storage ? readStorage(storage) : null;
  if (stored && !lastWriteFailed) return stored;
  // Storage unreadable or behind: this tab's copy wins for its own orders.
  const merged = [...memoryList];
  (stored || []).forEach((r) => { if (!merged.some((m) => m.orderId === r.orderId)) merged.push(r); });
  return merged;
};

const writeAll = (list, storage) => {
  memoryList = list;
  try {
    if (!storage) throw new Error("no localStorage");
    if (list.length) storage.setItem(IN_FLIGHT_KEY, JSON.stringify(list));
    else storage.removeItem(IN_FLIGHT_KEY);
    lastWriteFailed = false;
  } catch (e) {
    // full or blocked: the block holds in this tab, but not across a reload
    lastWriteFailed = true;
  }
  notify();
};

export const loadOne = (orderId, storage = defaultStorage()) =>
  loadAll(storage).find((r) => r.orderId === positiveId(orderId)) || null;

// Adds the entry, or replaces the one for the same order.
export const saveOne = (record, storage = defaultStorage()) =>
  writeAll([...loadAll(storage).filter((r) => r.orderId !== record.orderId), record], storage);

export const updateOne = (orderId, patch, storage = defaultStorage()) => {
  const list = loadAll(storage);
  if (!list.some((r) => r.orderId === positiveId(orderId))) return;
  writeAll(list.map((r) => (r.orderId === positiveId(orderId) ? { ...r, ...patch } : r)), storage);
};

export const removeOne = (orderId, storage = defaultStorage()) =>
  writeAll(loadAll(storage).filter((r) => r.orderId !== positiveId(orderId)), storage);

// When the guard's answer can next change on its own (an entry reaching a
// limit), so the screen can redraw then: the earliest future moment, or null.
export const nextBlockChange = (records, now) => {
  const limits = [];
  (records || []).forEach((r) => {
    if (r.paid) limits.push(markerEnd(r));
    else limits.push(r.startedAt + ACTIVE_MS, r.startedAt + STALE_AFTER_MS);
  });
  const future = limits.filter((t) => Number.isFinite(t) && t > now);
  return future.length ? Math.min(...future) : null;
};

// What to do with a saved entry at app start:
//   "resume"        still being checked (or not yet given up): check again
//                   now and carry on (the saga stops at the cap, counted
//                   from when the payment started)
//   "keep"          ended without an answer, or a paid marker: nothing to
//                   ask, it only keeps Pay blocked
//   "stale-show"    too old: drop it and show "check Orders" once
//   "stale-quiet"   too old, and that screen was already shown (or it was
//                   a paid marker): just drop it
export const resumeDecision = (record, now) => {
  if (!validRecord(record)) return "stale-quiet";
  if (record.paid) return now <= markerEnd(record) ? "keep" : "stale-quiet";
  if (now - record.startedAt <= STALE_AFTER_MS) return record.ended ? "keep" : "resume";
  return record.finalShown ? "stale-quiet" : "stale-show";
};

// Which tab checks which payment. Each tab of the panel has an id that
// survives its own reloads (sessionStorage); a polling tab stamps the entry
// with { pollerId, polledAt } before every check. An entry nobody has
// stamped for ORPHAN_MS (the polling tab was closed) is taken over by any
// other tab, so "still being checked" is never claimed without a check.
// ORPHAN_MS covers the longest gap: 60 s waiting for the payment request,
// then the first 10 s wait (and later: 10 s wait + a 20 s check).
export const ORPHAN_MS = 90 * 1000;

export const needsPoller = (record, now, tabId) => {
  if (resumeDecision(record, now) !== "resume") return false;
  if (record.pollerId && record.pollerId === tabId) return true;
  return now - (record.polledAt || record.startedAt) > ORPHAN_MS;
};

// ---------------------------------------------------------------------------
// 4. Double-pay guard.
//
// Paid marker: a "paid" answer, wherever it is seen, turns the entry into
// { paid: true, paidAt } and that CHE's Pay (mobile money and cash) stays
// blocked for PAID_BLOCK_MS, whatever the cart holds (one rule per CHE,
// shared by every tab; emptying a cart does not lift it).
// "Paid, order not confirmed" ({ paid: true, unconfirmed: true }) blocks
// that CHE until a check finds the order confirmed (it then becomes a
// confirmed marker, PAID_BLOCK_MS from then) or UNCONFIRMED_BLOCK_MS pass.

export const PAID_BLOCK_MS = 30 * 60 * 1000;
export const UNCONFIRMED_BLOCK_MS = 24 * 60 * 60 * 1000;
const markerEnd = (r) => (r.paidAt || r.startedAt) + (r.unconfirmed ? UNCONFIRMED_BLOCK_MS : PAID_BLOCK_MS);
// How long an entry that is not marked ended counts as "being checked" for
// other CHEs: the cap, the extra minute for "paid, not confirmed" and one
// slow request.
export const ACTIVE_MS = POLL_CAP_MS + UNCONFIRMED_GRACE_MS + 30 * 1000;

export const PAY_BLOCKED_SAME_CHE = "A payment for this CHE is still being checked.";
export const PAY_BLOCKED_OTHER_CHE = "A payment for another CHE is still being checked. Wait until it is done.";

// "14:05": local time, 24-hour clock.
export const clockTime = (ms) => {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// The line once checking has ended without an answer; the block lifts when
// the entry is dropped (STALE_AFTER_MS after the payment started).
export const payBlockedEndedText = (record) =>
  `Payment not confirmed. Check Orders. You can pay for this CHE again after ${clockTime(record.startedAt + STALE_AFTER_MS)}.`;

// "Paid, order not confirmed": the money was taken.
export const PAY_BLOCKED_UNCONFIRMED = "Payment received, but the order is not confirmed yet. Check Orders. Do not pay again.";

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
// "18:05" when `until` is today, else "Tuesday 09:00" (local time).
export const blockedUntilText = (until, now) => {
  const a = new Date(until);
  const b = new Date(now);
  const sameDay = a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  return sameDay ? clockTime(until) : `${WEEKDAYS[a.getDay()]} ${clockTime(until)}`;
};

// A confirmed payment: no new order for this CHE for PAID_BLOCK_MS.
export const payBlockedPaidText = (record) => {
  const order = record.orderName ? `order ${record.orderName}` : "an order";
  return `This CHE paid for ${order} at ${clockTime(record.paidAt)}. You can start a new order for this CHE after ${clockTime(markerEnd(record))}.`;
};

// Returns what to show instead of letting Pay go ahead, or null:
//   { text, orderId, checkAgain }   checkAgain: offer "Check again now"
//   records   the saved entries (loadAll), from every tab
//   cheId     the cart's CHE
//   method    the payment method picked ("cash", "ihela", ...)
// For the cart's own CHE every method is blocked, cash too: Pay sends a
// new order first, so cash would make a second order. An entry with no
// known CHE counts as every CHE's. Another CHE's payment blocks only mobile
// money, and only while it is being checked.
export const deskPayBlock = ({ inFrame, records, cheId, method, now }) => {
  if (!inFrame || !Array.isArray(records)) return null;
  const forThisChe = (r) => r.cheId === null || r.cheId === undefined || String(r.cheId) === String(cheId);
  let other = null;
  for (const r of records) {
    if (r.paid) {
      if (forThisChe(r) && now <= markerEnd(r)) {
        return r.unconfirmed
          ? { text: `${PAY_BLOCKED_UNCONFIRMED} Blocked until ${blockedUntilText(markerEnd(r), now)}.`, orderId: r.orderId, checkAgain: true }
          : { text: payBlockedPaidText(r), orderId: r.orderId, checkAgain: false };
      }
      continue;
    }
    if (now - r.startedAt > STALE_AFTER_MS) continue;
    if (forThisChe(r)) {
      return r.ended
        ? { text: payBlockedEndedText(r), orderId: r.orderId, checkAgain: true }
        : { text: PAY_BLOCKED_SAME_CHE, orderId: r.orderId, checkAgain: false };
    }
    if (!r.ended && now - r.startedAt <= ACTIVE_MS && !other) other = r;
  }
  return other && method !== "cash" ? { text: PAY_BLOCKED_OTHER_CHE, orderId: other.orderId, checkAgain: false } : null;
};

// ---------------------------------------------------------------------------
// 5. Where a definite answer goes: the app's existing result screens.

// Returns null when no order name is known (the page would show none): the
// panel's own screen is used instead.
export const resultPath = (done, body, fallbackName = null) => {
  const data = body?.data || {};
  const name = data.order?.display_name || data.order?.name || fallbackName || "";
  if (!name) return null;
  if (done === "paid") {
    const q = new URLSearchParams({
      msg: "Payment successful",
      delivery_start: data.route?.delivery_start || "",
      delivery_end: data.route?.delivery_end || "",
      transaction_id: data.payment?.transaction_id || "",
    });
    return `/cart/successful/${encodeURIComponent(name)}?${q.toString()}`;
  }
  return `/cart/failure/${encodeURIComponent(name)}?${new URLSearchParams({ msg: "Payment failed" }).toString()}`;
};

// ---------------------------------------------------------------------------
// 6. What the panel's payment screen shows (components/DeskPaymentStatus).

export const DESK_PAYMENT_COPY = {
  checking: "Ask the CHE to approve the payment on their phone. Do not press Pay again.",
  confirming: "Payment received. Waiting for the order to be confirmed. Do not pay again.",
  unconfirmed: "Payment received. The order is still being confirmed. Do not pay again.",
  timeout: "Payment not confirmed yet. Check Orders before trying again. Do not press Pay again.",
  paidOther: "This payment went through.",
  failedOther: "This payment did not go through.",
};

// Returns which screen to show, or null:
//   "waiting"  checking / confirming, unless the officer closed it
//   "unconfirmed", "timeout"  the unclear ends, once (finalShown)
//   "paid", "failed"  only left in state when the cart is another CHE's
//                     (otherwise the app's own result page opens)
// `hidden` is { orderId, phase } of the screen the officer closed.
export const deskPaymentScreen = (deskPayment, hidden) => {
  if (!deskPayment) return null;
  const phase = deskPayment.phase;
  if (phase === "checking" || phase === "confirming") {
    const closed = !!hidden && hidden.orderId === deskPayment.orderId && hidden.phase === phase;
    return closed ? null : "waiting";
  }
  if (phase === "unconfirmed") return deskPayment.finalShown ? null : "unconfirmed";
  if (phase === "timeout" || phase === "stale") return deskPayment.finalShown ? null : "timeout";
  if (phase === "paid" || phase === "failed") return deskPayment.navigating ? null : phase;
  return null;
};

// ---------------------------------------------------------------------------
// 7. Telling Route Desk.
//
// { type: "he-desk:payment-status", orderingFor: <CHE odoo id or null>,
//   state: "pending" | "paid" | "failed" | "unconfirmed" | null }
// null = no payment is being checked in this panel. Sent at start, after
// login-ready and whenever it changes.

export const PAYMENT_STATUS_TYPE = "he-desk:payment-status";

const ROUTE_DESK_STATE = {
  checking: "pending",
  confirming: "pending",
  paid: "paid",
  failed: "failed",
  unconfirmed: "unconfirmed",
  timeout: "unconfirmed",
  // "stale": a saved payment too old to check; only the screen is shown.
};

export const paymentStatusMessage = (deskPayment) => {
  const state = (deskPayment && ROUTE_DESK_STATE[deskPayment.phase]) || null;
  return {
    type: PAYMENT_STATUS_TYPE,
    orderingFor: state ? positiveId(deskPayment.cheId) : null,
    state,
  };
};

let resendCurrent = null;

// Called once at app start with the redux store; nothing outside a frame.
export const startDeskPaymentStatus = (store, { win = window } = {}) => {
  if (!win.parent || win.parent === win) return { unsubscribe: () => {}, resend: () => {} };
  let last = null;
  const send = (force) => {
    const msg = paymentStatusMessage(store.getState()?.payment?.deskPayment);
    if (!force && last && last.orderingFor === msg.orderingFor && last.state === msg.state) return;
    last = msg;
    postToRouteDesk(msg, win);
  };
  send(true);
  const unsubscribe = store.subscribe(() => send(false));
  const resend = () => send(true);
  resendCurrent = resend;
  return { unsubscribe, resend };
};

export const resendPaymentStatus = () => {
  if (resendCurrent) resendCurrent();
};

// ---------------------------------------------------------------------------
// 8. Which phone the payment request goes to.
//
// In the panel the signed-in account is always the officer's own employee
// account, so the payment form must not start with the signed-in user's
// number (that would send the request to the officer's phone). It starts
// with the CHE's mobile-money number instead, using the same order the
// phone app uses for its own user: default_phone_number (the number the CHE
// last paid or signed in with) before phone_number. Both come with the CHE
// row the cart belongs to (/user/ches, backend UserSerializer). No number:
// the field stays empty and must be filled in.

export const DESK_OWN_NUMBER = "This is your own number. Use the CHE's number.";

export const deskPayPhone = (che) => {
  const pick = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);
  return pick(che?.default_phone_number) || pick(che?.phone_number) || "";
};

const last9 = (value) => String(value || "").replace(/\D/g, "").slice(-9);

// True when the typed number is the signed-in officer's own (last 9 digits
// of phone_number or default_phone_number).
export const isOwnNumber = (typed, user) => {
  const t = last9(typed);
  if (t.length < 9) return false;
  return [user?.phone_number, user?.default_phone_number].some((own) => last9(own).length === 9 && last9(own) === t);
};

// ---------------------------------------------------------------------------
// 9. The CHE's numbers as a pick list (Thijs, 2026-10-14).
//
// Route Desk holds the CHE's Odoo `phone` and `mobile`. When the payment form
// opens in the panel it asks for them:
//   this app -> Route Desk: { type: "he-desk:che-phones-ready", orderingFor: <CHE odoo id> }
//   Route Desk -> this app: { type: "he-desk:che-phones", orderingFor, phones:
//                             [{ label, number: "+256..."|null, display }] }
//   label: "Phone" or "Mobile", optionally " (2nd)" / " (3rd)" when one Odoo
//   field held several numbers; at most six rows.
// Route Desk cleans the numbers (country code, mobile range); `number: null`
// means Odoo's value is not a valid number and is shown greyed, `display`
// being what Odoo holds. Accepted only from an allowed Route Desk origin, from
// the page that embeds us, and only for the CHE this cart is ordering for
// (matched on the Odoo partner id): never another CHE's numbers. No answer
// (an older Route Desk): the form stays the single field of section 8.
// The order app's own stored default_phone_number is not offered here.

export const CHE_PHONES_ASK_TYPE = "he-desk:che-phones-ready";
export const CHE_PHONES_TYPE = "he-desk:che-phones";
export const DESK_PICK_OTHER = "other";
export const DESK_NOT_VALID = "not a valid number";
export const DESK_PICK_NONE = "Choose a number, or type another one.";

// "Phone", "Phone (2nd)", "Phone (3rd)", the same for "Mobile": Route Desk
// lists each number in a field holding several as its own choice.
const PICK_LABEL = /^(Phone|Mobile)( \((2nd|3rd)\))?$/;
const PICK_MAX = 6;

export const askChePhones = (cheId, win = window) => {
  const id = positiveId(cheId);
  if (id) postToRouteDesk({ type: CHE_PHONES_ASK_TYPE, orderingFor: id }, win);
};

// The cleaned list from a Route Desk answer for `cheId`, or null when the
// message is not one (wrong sender, wrong type, another CHE). Entries that do
// not have the agreed shape are dropped; a number seen before is listed once.
export const chePhonesFromMessage = (event, cheId, win = window) => {
  const data = routeDeskData(event, win);
  if (!data || data.type !== CHE_PHONES_TYPE || !Array.isArray(data.phones)) return null;
  const id = positiveId(cheId);
  if (!id || positiveId(data.orderingFor) !== id) return null;
  const phones = [];
  data.phones.forEach((p) => {
    if (phones.length >= PICK_MAX) return;
    if (!p || typeof p.label !== "string" || !PICK_LABEL.test(p.label) || typeof p.display !== "string" || !p.display.trim()) return;
    const number = typeof p.number === "string" && PHONE_PATTERN.test(p.number) ? p.number : null;
    if (p.number != null && !number) return;
    if (phones.some((q) => q.label === p.label || (number && q.number === number))) return;
    phones.push({ label: p.label, number, display: p.display.trim().slice(0, 40) });
  });
  return phones;
};

// The choice the form starts on: the first valid Phone number (of any part),
// else the first valid Mobile one, else the open field.
export const defaultPhoneChoice = (phones) => {
  const valid = (field) => (phones || []).find((p) => p.number && p.label.startsWith(field));
  const pick = valid("Phone") || valid("Mobile");
  return pick ? pick.number : DESK_PICK_OTHER;
};

// The choice after (another) answer: the officer's current one when it is
// still possible (a valid listed number, or the open field), else the default.
export const keptPhoneChoice = (current, phones) => {
  if (current === DESK_PICK_OTHER) return current;
  if (current && (phones || []).some((p) => p.number === current)) return current;
  return defaultPhoneChoice(phones);
};

// What was typed in the open field, without spaces, dashes, dots or brackets
// (a leading + is kept): "0772 123-456" -> "0772123456".
export const typedDigits = (typed) => (typeof typed === "string" ? typed.replace(/[\s\-().]/g, "") : "");

// The number the payment request goes to: the chosen one, or the typed one
// with the country prefix (the app's own prefixing). "" when there is none.
// A choice that is not one of the valid listed numbers counts as none.
export const chosenPhone = (choice, typed, phones, prefixTyped) => {
  if (choice === DESK_PICK_OTHER) {
    const t = typedDigits(typed);
    return t ? prefixTyped(t) : "";
  }
  return (phones || []).some((p) => p.number && p.number === choice) ? choice : "";
};
